import { type FormEvent, useState } from 'react';
import { i18n, type PalletState } from '@wms/shared';
import { Dialog } from './Dialog.js';

const { t } = i18n;

export type BulkPalletTargetState = PalletState | 'DEPLETED';

function targetLabel(state: BulkPalletTargetState): string {
  return state === 'DEPLETED' ? t('pallet.depleted.badge') : t(`pallet.state.${state}`);
}

export function BulkPalletTransitionDialog({
  palletCodes,
  targetStates,
  busy,
  onClose,
  onConfirm,
}: {
  palletCodes: readonly string[];
  targetStates: readonly BulkPalletTargetState[];
  busy: boolean;
  onClose: () => void;
  onConfirm: (toState: BulkPalletTargetState, reason: string, date: string) => void | Promise<void>;
}) {
  const [toState, setToState] = useState<BulkPalletTargetState | ''>('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!toState) {
      setError(t('pallet.bulk_transition.state_required'));
      return;
    }
    if (!trimmed) {
      setError(t('pallet.transition.reason_required'));
      return;
    }
    setError(null);
    await onConfirm(toState, trimmed, date);
  }

  return (
    <Dialog title={t('pallet.bulk_transition.title')} onClose={onClose}>
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-3 text-sm">
        <div className="rounded border border-border bg-muted/40 px-3 py-2">
          <div className="font-medium">
            {t('pallet.bulk_transition.selected').replace('{{count}}', String(palletCodes.length))}
          </div>
          <div className="mt-1 max-h-24 overflow-y-auto font-mono text-xs text-muted-foreground">
            {palletCodes.join(', ')}
          </div>
        </div>

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.bulk_transition.state_label')}</span>
          <select
            value={toState}
            onChange={(event) => setToState(event.target.value as BulkPalletTargetState)}
            required
            autoFocus
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="">{t('common.select')}</option>
            {targetStates.map((state) => (
              <option key={state} value={state}>
                {targetLabel(state)}
              </option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.transition.reason_label')}</span>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={4}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.op_date.label')}</span>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('pallet.bulk_transition.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
